/**
 * Power Distribution section descriptor (#997 — Story 4.4).
 *
 * The real, live `SectionDescriptor` for the Power Distribution dynamic
 * section. It mirrors the Large UPS descriptor (Story 4.3) but binds the
 * ACTUAL Power Distribution per-unit field set, table, mapper, REST contract,
 * and attachment card, so the shared descriptor engine can drive Power
 * Distribution at byte-identical parity with the pre-engine bespoke
 * implementation (Story 4.4 golden masters are the oracle).
 *
 * Field source of truth: `FACILITY_POWER_FIELDS` (`facility-power.fields.ts`)
 * plus the editor slice at `POWER_DISTRIBUTION_EDITOR_SUBSECTION_FIELDS[2..]`
 * — every per-unit field is typed here (text / multilineText / number) to
 * match the real controls in `PowerDistributionEditFormContent.tsx`. `name`
 * (user-defined instance name) and `status` (Existing/New) are the engine's
 * identity fields and are handled by the engine list chrome, so they are NOT
 * per-unit descriptor `fields` (they are added back only on the Summary
 * surface via the identity-field label bridge in
 * `powerDistributionDescriptorSummary`).
 *
 * `powerAdditionalInformation` is a PER-UNIT "Additional Information" note on
 * `PowerDistributionConfiguration` (#997 Bug 2 semantics via migration 109),
 * so it lives on the per-unit fields list and is declared via
 * `additionalInfoField`. Power Distribution has NO shared request-level field
 * (mirrors LU; contrast LC's `lcLiquidToChipDesired`), so `sharedFieldKeys` is
 * `[]`.
 *
 * NFR7: the table (`request_power_distribution`), mapper
 * (`power-distribution-configuration.mapper`), and permission config are
 * read-only; this descriptor only DECLARES the binding.
 */

import { locationTagDescriptorField } from './sectionDescriptor.types';
import {
  POWER_DISTRIBUTION_TABLE_COLUMN_ALIGN,
  powerDistributionColumnUsesDoubleLine,
  powerDistributionTableColumnsForLayout,
  powerDistributionTableHeaderLabel,
  formatPowerDistributionTableCell,
  getPowerDistributionColumnSubline,
} from '../../components/powerdistribution/powerDistributionListUtils';
import {
  POWER_DISTRIBUTION_CONFIGURATIONS_LIST_TITLE,
  POWER_DISTRIBUTION_DELETE_CONFIRM_MESSAGE,
  POWER_DISTRIBUTION_DELETE_CONFIRM_TITLE,
  POWER_DISTRIBUTION_DISCARD_UNSAVED_DISCARD_LABEL,
  POWER_DISTRIBUTION_DISCARD_UNSAVED_KEEP_EDITING_LABEL,
  POWER_DISTRIBUTION_DISCARD_UNSAVED_MESSAGE,
  POWER_DISTRIBUTION_DISCARD_UNSAVED_TITLE,
  POWER_DISTRIBUTION_EDIT_ALL_UNITS_LABEL,
  POWER_DISTRIBUTION_EDIT_DELETE_LABEL,
  POWER_DISTRIBUTION_EDIT_HEADER_TITLE_NEW,
  POWER_DISTRIBUTION_EDIT_SAVE_DRAFT_LABEL,
  POWER_DISTRIBUTION_EDIT_SAVE_LABEL,
} from '../../constants/powerDistributionConfigurationsSubsectionCopy';
import { SECTION_IDS } from '../../constants/sectionIds';
import { POWER_DISTRIBUTION_EDITOR_SUBSECTION_FIELDS } from '../fields/requestFieldRegistry';
import { FACILITY_POWER_FIELDS } from '../fields/steps/facility-power.fields';

import type {
  SectionDescriptor,
  SectionDescriptorField,
  SectionDescriptorFieldType,
  SectionDescriptorFieldValidation,
  SectionListColumnsProjection,
} from './sectionDescriptor.types';
import type { PowerDistributionTableColumnKey } from '../../components/powerdistribution/powerDistributionListUtils';
import type { PowerDistributionConfiguration } from '../../types/powerDistribution';

/**
 * Per-field render type + validation + dropdown options, keyed by dataField.
 * Every non-identity per-unit key covered by the descriptor has an entry here;
 * anything missing falls back to `text`. Types match the real Power
 * Distribution controls (see `PowerDistributionEditFormContent`):
 *
 *   - `powerUpsOutputVoltage` — FormInput (text) at :264
 *   - `powerDistributionVoltageToLoad` — FormInput (text) at :282
 *   - `powerOverheadBuswayOrUnderFloor` — FormInput (text) at :305 — despite
 *     the question wording ("Is a busway or under floor cabling to rack
 *     desired?"), the live control is a free-text `FormInput`, NOT
 *     `FormYesNoChoice`. Descriptor MUST match the live control (not the
 *     wording semantics) or types will not round-trip.
 *   - `powerBuswayAmperage` — FormInput (text) at :325
 *   - `powerBuswayLengthFt` — FormInput numeric with `parseOptionalNumber` at
 *     :346. Typed `text` here (NOT `number`) because the live control IS a
 *     free-text `FormInput` with client-side coercion — matching the live
 *     control preserves round-trip parity for the deferred live-editor swap
 *     (mirrors LU's `powerBatteryRuntimeRequiredMin: text` precedent).
 *   - `powerTapOfBoxRequirements` — FormInput multiline `maxLength={10000}` at
 *     :369 (multilineText)
 *   - `powerAdditionalInformation` — `RepeatableInstanceAdditionalInfo` at
 *     :388 (multilineText)
 */
interface PowerDistributionFieldTypeSpec {
  type: SectionDescriptorFieldType;
  options?: readonly string[];
  validation?: SectionDescriptorFieldValidation;
}

const POWER_DISTRIBUTION_FIELD_TYPES: Readonly<
  Record<string, PowerDistributionFieldTypeSpec>
> = {
  powerUpsOutputVoltage: { type: 'text' },
  powerDistributionVoltageToLoad: { type: 'text' },
  powerOverheadBuswayOrUnderFloor: { type: 'text' },
  powerBuswayAmperage: { type: 'text' },
  powerBuswayLengthFt: { type: 'text' },
  powerTapOfBoxRequirements: {
    type: 'multilineText',
    validation: { maxLength: 10000 },
  },
  // Per-unit "Additional Information" note (#997 Bug 2, migration 109)
  powerAdditionalInformation: { type: 'multilineText' },
};

/** Registry lookup: dataField → RequestFieldDefinition (for label / subsection). */
const POWER_DISTRIBUTION_FIELDS_BY_DATA_FIELD = new Map(
  FACILITY_POWER_FIELDS.map(field => [field.dataField, field]),
);

/** Identity fields the engine list chrome owns; NOT in per-unit `fields`. */
const POWER_DISTRIBUTION_IDENTITY_KEYS = new Set(['name', 'status']);

/**
 * Per-unit descriptor field dataField keys, in editor order. Sourced from
 * `POWER_DISTRIBUTION_EDITOR_SUBSECTION_FIELDS` minus identity fields (`name`,
 * `status`), plus the note field (`powerAdditionalInformation`) appended.
 */
const PER_UNIT_DATA_FIELD_KEYS: readonly string[] = [
  ...POWER_DISTRIBUTION_EDITOR_SUBSECTION_FIELDS.filter(
    field => !POWER_DISTRIBUTION_IDENTITY_KEYS.has(field.key),
  ).map(field => field.key),
  'powerAdditionalInformation',
];

function buildDescriptorField(dataField: string): SectionDescriptorField {
  if (dataField === 'locationTagId') {
    return locationTagDescriptorField(
      'power-distribution',
      'powerDistribution',
    );
  }
  const registryField = POWER_DISTRIBUTION_FIELDS_BY_DATA_FIELD.get(dataField);
  if (!registryField) {
    throw new Error(
      `POWER_DISTRIBUTION_DESCRIPTOR: no FACILITY_POWER_FIELDS entry for ${dataField}`,
    );
  }
  const spec = POWER_DISTRIBUTION_FIELD_TYPES[dataField] ?? { type: 'text' };
  return {
    ...registryField,
    type: spec.type,
    options: spec.options,
    validation: spec.validation,
  };
}

/** Typed per-unit descriptor fields (7: 6 per-unit fields + 1 note). */
const POWER_DISTRIBUTION_DESCRIPTOR_FIELDS: readonly SectionDescriptorField[] =
  PER_UNIT_DATA_FIELD_KEYS.map(buildDescriptorField);

/**
 * Snake_case columns the Power Distribution section persists — the exact key
 * set emitted by `power-distribution-configuration.mapper.toEntity` and
 * (excluding identity/audit columns) the `request_power_distribution`
 * migration 103 + 109 column union. The `descriptorDbBinding` guardrail joins
 * this list to both.
 */
export const POWER_DISTRIBUTION_DESCRIPTOR_DB_COLUMNS: readonly string[] = [
  'request_id',
  'status',
  'name',
  'location_tag_id',
  'power_ups_output_voltage',
  'power_distribution_voltage_to_load',
  'power_overhead_busway_or_under_floor',
  'power_busway_amperage',
  'power_busway_length_ft',
  'power_tap_of_box_requirements',
  'power_additional_information',
  'display_order',
];

/**
 * Power Distribution list projection (#997 Story 6.7 — parity by reuse). Delegates
 * to the product-approved bespoke table (`powerDistributionListUtils`: NAME |
 * BUSWAY | AMPERAGE | LENGTH | VOLTAGE + compact set, sublines, double-line cells).
 * Engine rows carry identity as `instanceName`/`instanceStatus` (stripped from the
 * PD shape by `powerDistributionToDescriptorInstance`), so adapt the row back before
 * the PD-shaped formatters run — the exact `largeUpsRowForList` /
 * `coolingSystemRowForList` pattern.
 */
const powerDistributionRowForList = (
  row: Record<string, unknown>,
): PowerDistributionConfiguration => {
  const unit = row as unknown as PowerDistributionConfiguration;
  return {
    ...unit,
    name:
      unit.name ?? (row.instanceName as PowerDistributionConfiguration['name']),
    status:
      unit.status ??
      (row.instanceStatus as PowerDistributionConfiguration['status']),
  };
};

const POWER_DISTRIBUTION_LIST_COLUMNS: SectionListColumnsProjection = {
  locationTagCellColumn: 'ROOM',
  columnsForLayout: powerDistributionTableColumnsForLayout,
  headerLabel: (column, layout) =>
    powerDistributionTableHeaderLabel(
      column as PowerDistributionTableColumnKey,
      layout,
    ),
  formatCell: (row, column) =>
    formatPowerDistributionTableCell(
      powerDistributionRowForList(row),
      column as PowerDistributionTableColumnKey,
    ),
  columnAlign: POWER_DISTRIBUTION_TABLE_COLUMN_ALIGN,
  getColumnSubline: (row, column, layout) =>
    getPowerDistributionColumnSubline(
      column as PowerDistributionTableColumnKey,
      powerDistributionRowForList(row),
      layout,
    ),
  columnUsesDoubleLine: (column, layout) =>
    powerDistributionColumnUsesDoubleLine(
      column as PowerDistributionTableColumnKey,
      layout,
    ),
};

/** The canonical Power Distribution descriptor. */
export const POWER_DISTRIBUTION_DESCRIPTOR: SectionDescriptor = {
  id: 'power-distribution',
  stepId: 'power-distribution',
  labels: {
    entitySingular: 'Power Distribution',
    entityPlural: 'Power Distribution',
    addButtonLabel: 'Add Power Distribution',
    emptyStateTitle: 'No Power Distribution added yet',
    editFormTitle: POWER_DISTRIBUTION_EDIT_HEADER_TITLE_NEW,
    editFormSubtitle: POWER_DISTRIBUTION_CONFIGURATIONS_LIST_TITLE,
    // Chrome copy parity (#997 Story 6.7, mirror Cooling 6.6 / Large UPS 6.5):
    // bind the bespoke hand-authored copy so the engine chrome is byte-identical
    // to the pre-swap subsection, not template-coincident.
    editSaveLabel: POWER_DISTRIBUTION_EDIT_SAVE_LABEL,
    editSaveDraftLabel: POWER_DISTRIBUTION_EDIT_SAVE_DRAFT_LABEL,
    editDeleteLabel: POWER_DISTRIBUTION_EDIT_DELETE_LABEL,
    deleteConfirmTitle: POWER_DISTRIBUTION_DELETE_CONFIRM_TITLE,
    deleteConfirmMessage: POWER_DISTRIBUTION_DELETE_CONFIRM_MESSAGE,
    editAllListLabel: POWER_DISTRIBUTION_EDIT_ALL_UNITS_LABEL,
    editDiscardTitle: POWER_DISTRIBUTION_DISCARD_UNSAVED_TITLE,
    editDiscardMessage: POWER_DISTRIBUTION_DISCARD_UNSAVED_MESSAGE,
    editDiscardConfirmLabel: POWER_DISTRIBUTION_DISCARD_UNSAVED_DISCARD_LABEL,
    editDiscardKeepEditingLabel:
      POWER_DISTRIBUTION_DISCARD_UNSAVED_KEEP_EDITING_LABEL,
    newInstanceChromeTitle: POWER_DISTRIBUTION_EDIT_HEADER_TITLE_NEW,
  },
  cardinality: 'many',
  fields: POWER_DISTRIBUTION_DESCRIPTOR_FIELDS,
  permissionRef: {
    rbacAnchorDataField: 'powerUpsOutputVoltage',
  },
  summaryOrder: POWER_DISTRIBUTION_DESCRIPTOR_FIELDS.map(
    field => field.dataField,
  ),
  pdfLayout: {
    stepId: 'power-distribution',
    anchor: 'Power Distribution Information',
    perUnitFieldGroupKeys: ['powerDistributionInfoFields'],
  },
  // Power Distribution has NO request-level shared fields — no analog to LC's
  // `lcLiquidToChipDesired`. Mirrors LU.
  sharedFieldKeys: [],
  // The live PD editor renders TWO per-unit attachment carousels: the details
  // card at `SECTION_IDS.FACILITY_POWER_DISTRIBUTION` AND the per-unit
  // Additional Information card at
  // `SECTION_IDS.FACILITY_POWER_DISTRIBUTION_ADDITIONAL`, both scoped through
  // `resolveRepeatableInstanceAttachmentSectionId(base, cfgId)`
  // (`PowerDistributionEditFormContent.tsx`). Bug 1 / Obs-7 (#997) fixed the
  // additional card, which previously used a flat unit-agnostic id shared
  // across units. Ordering matters: `DescriptorInstanceEditFormBody` derives
  // the per-unit Additional Info section id from
  // `attachmentBaseSectionIds[length - 1]`, so `_ADDITIONAL` MUST be last
  // (mirrors LU / Cooling).
  attachmentsEnabled: true,
  attachmentBaseSectionIds: [
    SECTION_IDS.FACILITY_POWER_DISTRIBUTION,
    SECTION_IDS.FACILITY_POWER_DISTRIBUTION_ADDITIONAL,
  ],
  additionalInfoField: 'powerAdditionalInformation',
  listColumns: POWER_DISTRIBUTION_LIST_COLUMNS,
  offlineBinding: {
    restResource: 'power-distribution',
    // Power Distribution is the FIFTH and FINAL section wired onto the engine
    // save/flush path (#997 Story 6.7). It opts in explicitly via the delegating
    // flush shim `flushPowerDistributionDescriptorEditSession` (see
    // `resolveDelegatedFlushBinding`).
    delegatedFlush: true,
    reorderIdsBodyKey: 'powerDistributionIds',
    persistedInstancesKey: 'powerDistributionConfigurations',
    instanceIdOpField: 'powerDistributionId',
    reorderIdsOpField: 'powerDistributionIds',
  },
  dbBinding: {
    table: 'request_power_distribution',
    columns: POWER_DISTRIBUTION_DESCRIPTOR_DB_COLUMNS,
  },
};
